/**
 * Job types for ATS.
 */

export interface JobPosition {
  id: string;
  name: string;
}

export interface JobCampaign {
  id: string;
  name: string;
}

export interface JobPipeline {
  id: string;
  name: string;
}

export interface JobEmployeeType {
  id: string;
  name: string;
}

export interface JobRecruitmentType {
  id: string;
  name: string;
}

export interface JobWorkArrangement {
  id: string;
  name: string;
}

export type JobStatus = 'open' | 'closed' | 'on_hold';

export interface Job {
  id: string;
  code: string;
  position: JobPosition;
  campaign: JobCampaign;
  pipeline: JobPipeline | null;
  employee_type: JobEmployeeType | null;
  recruitment_type: JobRecruitmentType | null;
  work_arrangement: JobWorkArrangement | null;
  quantity: number;
  status: JobStatus;
  start_date: string | null;
  end_date: string | null;
  note: string | null;
  created_at: string;
  cv_count: number;
}

export interface JobListResponse {
  items: Job[];
  total: number;
}

export const JOB_STATUS_CONFIG: Record<
  JobStatus,
  { label: string; color: 'green' | 'gray' | 'yellow' }
> = {
  open: { label: 'Open', color: 'green' },
  closed: { label: 'Closed', color: 'gray' },
  on_hold: { label: 'On Hold', color: 'yellow' },
};

// Kanban board types

export interface CVJobSource {
  id: string;
  name: string;
}

export interface CVJobRejectionReason {
  id: string;
  reason: string;
}

export interface JobCVCard {
  id: string;
  name: string;
  processing_status: string;
  is_rejected: boolean;
  rejection_reason: CVJobRejectionReason | null;
  rejection_reason_text: string | null;
  source: CVJobSource | null;
  applied_at: string | null;
  onboard_date: string | null;
  created_at: string;
  // Additional info for display
  email: string | null;
  phone: string | null;
  note_count: number;
  linked_at: string;
  person_in_charge_id: string | null;
}

export interface JobKanbanColumn {
  id: string;
  name: string;
  round_type: string | null;
  round_type_label: string | null;
  order: number;
  cvs: JobCVCard[];
}

export interface JobDetail extends Job {
  columns: JobKanbanColumn[];
}

// Input types for create/update
export interface JobCreateInput {
  code: string;
  position_id: string;
  campaign_id: string;
  pipeline_id: string;
  employee_type_id?: string | null;
  recruitment_type_id?: string | null;
  work_arrangement_id?: string | null;
  quantity?: number;
  status?: JobStatus;
  start_date?: string | null;
  end_date?: string | null;
  note?: string | null;
  assignee_ids?: string[] | null;
}

export interface JobUpdateInput {
  code?: string;
  position_id?: string;
  campaign_id?: string;
  pipeline_id?: string | null;
  employee_type_id?: string | null;
  recruitment_type_id?: string | null;
  work_arrangement_id?: string | null;
  quantity?: number;
  status?: JobStatus;
  start_date?: string | null;
  end_date?: string | null;
  note?: string | null;
}

// CV-Job update input
export interface CVJobUpdateInput {
  round_id?: string; // Required to be a valid round (no null/inbox)
  is_rejected?: boolean;
  rejection_reason_id?: string | null;
  rejection_reason_text?: string | null;
  source_id?: string | null;
  applied_at?: string | null;
  onboard_date?: string | null;
}

// Bulk CV-Job update input
export interface BulkCVJobUpdateInput {
  cv_ids: string[];
  round_id?: string;
  is_rejected?: boolean;
  rejection_reason_id?: string | null;
  rejection_reason_text?: string | null;
}
